import {
    AssertionDescriptorDisplayValueTypeEnum,
    AssertionDescriptorValueSourceTypeEnum,
    AssertionDescriptors,
    CalculatedAgeAssertionDescriptorValues,
    CaseFileStaticItems,
    CaseStatusTypesEnum,
    CaseSubStatusTypesEnum,
    CustomLogicAssertionDescriptors,
    DataScopesEnum,
    HelpKeys,
    QueryFilterDatabaseFieldsHelpText,
    QueryTypes,
    UserRightsEnum,
} from './Constants';
import {
    IAssertionDescriptorModel,
    IAssertionDescriptorValueModel,
} from '../interfaces/IAssertionDescriptor';
import { ICaseAssertionModel, ICaseModel, ICaseTitleModel } from '../interfaces/ICase';
import { ILookupModel, IMultiSelectOption, IStaticItemLookupModel } from '../interfaces/ILookup';
import {
    SameCaseApprovedHelperText,
    SameCasePendingHelperText,
} from '../pages/quad/same-cases-admin/manage/useGetSameCasesQuery';

import Authorization from '../stores/Authorization';
import Common from '../stores/Common';
import { IOrganizationSettingModel } from '../interfaces/IOrganization';
import { IUserModel } from '../interfaces/IUser';
import { cloneDeep } from 'lodash';

export default class CaseHelper {
    static isResolvedCase(currentCase: ICaseModel): boolean {
        return currentCase.caseStatus?.status?.id === CaseStatusTypesEnum.Resolved.Value;
    }

    static isTenderedAcceptedCase(currentCase: ICaseModel): boolean {
        return (
            currentCase.caseStatus?.status?.id === CaseStatusTypesEnum.Tendered.Value &&
            currentCase.caseStatus?.subStatus?.id === CaseSubStatusTypesEnum.Accepted.Value
        );
    }

    static isAltMonitoredCase(currentCase: ICaseModel): boolean {
        return (
            currentCase.caseStatus?.status?.id === CaseStatusTypesEnum.Alt.Value &&
            currentCase.caseStatus.subStatus?.id === CaseSubStatusTypesEnum.Monitored.Value
        );
    }

    static isAltCase(currentCase: ICaseModel): boolean {
        return currentCase.caseStatus?.status?.id === CaseStatusTypesEnum.Alt.Value;
    }

    static getAdditionalCaseAssertionFields = (
        caseItem: ICaseModel,
        assertionDescriptors: IAssertionDescriptorModel[]
    ): ICaseAssertionModel[] => {
        const caseItemCopy = cloneDeep(caseItem);
        const additionalCaseAssertions: ICaseAssertionModel[] = [];
        if (!caseItemCopy.caseAssertions) caseItemCopy.caseAssertions = [];

        let adGuidsForAdditionalFields: string[] = [];
        if (CaseHelper.isResolvedCase(caseItemCopy)) {
            adGuidsForAdditionalFields = [
                AssertionDescriptors.ResolutionAgreementDate.Guid,
                AssertionDescriptors.DismissalDate.Guid,
                AssertionDescriptors.SettlementAmount.Guid,
                AssertionDescriptors.StatusDocumented.Guid,
            ];
        } else if (CaseHelper.isAltCase(caseItemCopy)) {
            adGuidsForAdditionalFields = [
                AssertionDescriptors.ResolutionAgreementDate.Guid,
                AssertionDescriptors.DismissalDate.Guid,
                AssertionDescriptors.StatusDocumented.Guid,
            ];
        } else if (CaseHelper.isTenderedAcceptedCase(caseItemCopy)) {
            adGuidsForAdditionalFields = [AssertionDescriptors.TenderedAcceptedDate.Guid];
        }

        for (let i = 0; i < adGuidsForAdditionalFields.length; i++) {
            const caseAssertionMatch = caseItemCopy.caseAssertions.find(
                (x) => x.assertionDescriptorGuid?.toUpperCase() === adGuidsForAdditionalFields[i]
            );

            if (caseAssertionMatch) {
                additionalCaseAssertions.push(caseAssertionMatch);
            } else {
                const adMatch = assertionDescriptors.find(
                    (ad) => ad.guid?.toUpperCase() === adGuidsForAdditionalFields[i]
                );

                if (adMatch) {
                    additionalCaseAssertions.push({
                        assertionDescriptorGuid: adMatch.guid,
                    });
                }
            }
        }

        return additionalCaseAssertions;
    };

    static getDocumentedStatusCaseAssertion = (caseItem: ICaseModel) => {
        if (caseItem.caseAssertions) {
            const match = caseItem.caseAssertions.find(
                (x) =>
                    x.assertionDescriptorGuid?.toUpperCase() ===
                    AssertionDescriptors.StatusDocumented.Guid.toUpperCase()
            );
            return match;
        }
    };

    static getDocumentedStatusAssertionDescriptor = (
        assertionDescriptors: IAssertionDescriptorModel[]
    ) => {
        const match = assertionDescriptors.find(
            (x) => x.name!.toUpperCase() == AssertionDescriptors.StatusDocumented.Name.toUpperCase()
        );
        return match;
    };

    static getCaseNotesHelpText = (organizationSettings: IOrganizationSettingModel[]) => {
        let helpText: ILookupModel[] = [];
        const match = organizationSettings.find((x) => x.settingName == HelpKeys.CaseNotes.Name);
        if (match) helpText = JSON.parse(match.settingValue);
        return helpText;
    };

    static getAssertionTextByGuid(
        caseAssertions: ICaseAssertionModel[],
        assertionDescriptorGuid: string,
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        if (caseAssertions) {
            const match = caseAssertions.find(
                (x) =>
                    x.assertionDescriptorGuid?.toUpperCase() ===
                    assertionDescriptorGuid.toUpperCase()
            );

            const foundAD = assertionDescriptors.find(
                (ad) => ad.guid === match?.assertionDescriptorGuid
            );

            switch (foundAD?.valueSourceType?.id) {
                case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                    {
                        const foundADV = foundAD?.assertionDescriptorValues?.find(
                            (adv) => adv.guid === match?.assertionDescriptorValueGuid
                        );

                        if (match) {
                            return foundADV?.text;
                        }
                    }
                    break;
                case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                    switch (foundAD?.valueDisplayType?.id) {
                        case AssertionDescriptorDisplayValueTypeEnum.DateTime.Value:
                            if (match?.dateTimeValue)
                                return Common.safeDateFormat(match?.dateTimeValue);
                            break;
                        case AssertionDescriptorDisplayValueTypeEnum.Number.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.Decimal.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.Percentage.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.Currency.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value:
                            if (match?.numericValue) return match?.numericValue.toString();
                            break;
                        case AssertionDescriptorDisplayValueTypeEnum.ShortText.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.Narrative.Value:
                        case AssertionDescriptorDisplayValueTypeEnum.SSN.Value:
                            return match?.textValue;
                        default:
                            break;
                    }
                    break;
                case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                    if (match?.booleanValue) return match?.booleanValue.toString();
                    break;
                default:
                    break;
            }
        }

        return '';
    }

    static getMultiSelectOptions = (ad: IAssertionDescriptorModel) => {
        const options: IMultiSelectOption[] = [];
        ad.assertionDescriptorValues!.map((item: IAssertionDescriptorValueModel) => {
            return options.push({ label: item.text!, value: item.guid!, id: 0, guid: item.guid });
        });
        return options;
    };

    static getMultiSelectSelectedValues = (
        ad: IAssertionDescriptorModel,
        currentCase?: ICaseModel
    ) => {
        const selectedValues: IMultiSelectOption[] = [];
        if (currentCase?.caseAssertions) {
            const match = currentCase.caseAssertions.filter(
                (x) => x.assertionDescriptorGuid === ad.guid && x.assertionDescriptorValueGuid
            );
            for (let i = 0; i < match.length; i++) {
                const item: ICaseAssertionModel = match[i];
                const foundADV = ad.assertionDescriptorValues?.find(
                    (adv) => adv.guid === item.assertionDescriptorValueGuid
                );
                if (foundADV)
                    selectedValues.push({
                        label: foundADV.text!,
                        value: foundADV.guid!,
                        id: 0,
                        guid: foundADV.guid!,
                    });
            }
        }
        return selectedValues;
    };

    static getMultiSelectSelectedValuesTitleText(selectedValues: IMultiSelectOption[]) {
        let selectedValuesTitleText = '';
        if (selectedValues.length > 1) {
            selectedValues.map((item: IMultiSelectOption) => {
                return (selectedValuesTitleText += item.label + '\n');
            });
        }

        return selectedValuesTitleText;
    }

    static getCalculatedAgeAssertionDescriptorValueGuid(age: number): string {
        if (age > 0) {
            const match = CalculatedAgeAssertionDescriptorValues.find(
                (x) => age >= x.From && age <= x.To
            );
            if (match) return match.Guid;
        }
        return '';
    }

    static setStyleAsSystemAutoPopulated(
        caseAssertion: ICaseAssertionModel,
        currentCase: ICaseModel
    ): boolean {
        if (!caseAssertion || !caseAssertion.assertionDescriptorGuid) return false;

        if (this.isFieldReadOnly(caseAssertion.assertionDescriptorGuid || '', currentCase)) {
            return true;
        }

        if (
            CustomLogicAssertionDescriptors.find(
                (x) =>
                    x.Guid.toUpperCase() === caseAssertion.assertionDescriptorGuid?.toUpperCase() &&
                    x.AutoCalculated
            ) &&
            !caseAssertion.userOverride &&
            (caseAssertion.assertionDescriptorValueGuid ||
                caseAssertion.textValue ||
                caseAssertion.booleanValue ||
                caseAssertion.dateTimeValue ||
                caseAssertion.numericValue)
        ) {
            return true;
        }

        return false;
    }

    static isFieldReadOnly(assertionDescriptorGuid: string, currentCase: ICaseModel): boolean {
        if (
            CustomLogicAssertionDescriptors.some(
                (x) => x.Guid.toUpperCase() == assertionDescriptorGuid?.toUpperCase() && x.ReadOnly
            )
        ) {
            return true;
        }

        switch (assertionDescriptorGuid.toUpperCase()) {
            case AssertionDescriptors.LivingDeceased.Guid.toUpperCase():
                if (currentCase.caseAssertions) {
                    const dateOfDeathAssertion = currentCase.caseAssertions.find(
                        (x) =>
                            x.assertionDescriptorGuid?.toUpperCase() ===
                            AssertionDescriptors.DateOfDeath.Guid.toUpperCase()
                    );
                    if (
                        dateOfDeathAssertion?.dateTimeValue &&
                        Common.isValidDate(dateOfDeathAssertion?.dateTimeValue.toString())
                    ) {
                        return true;
                    }
                }
                break;
            case AssertionDescriptors.EstimatedAge.Guid.toUpperCase():
                if (currentCase.caseAssertions) {
                    const dobAssertion = currentCase.caseAssertions.find(
                        (x) =>
                            x.assertionDescriptorGuid?.toUpperCase() ===
                            AssertionDescriptors.DOB.Guid.toUpperCase()
                    );
                    const dateOfDeathAssertion = currentCase.caseAssertions.find(
                        (x) =>
                            x.assertionDescriptorGuid?.toUpperCase() ===
                            AssertionDescriptors.DateOfDeath.Guid.toUpperCase()
                    );
                    if (
                        dobAssertion &&
                        dateOfDeathAssertion &&
                        dobAssertion.dateTimeValue &&
                        dateOfDeathAssertion.dateTimeValue &&
                        Common.isValidDate(dobAssertion.dateTimeValue.toString()) &&
                        Common.isValidDate(dateOfDeathAssertion.dateTimeValue.toString())
                    ) {
                        return true;
                    }

                    if (
                        dobAssertion?.dateTimeValue &&
                        Common.isValidDate(dobAssertion?.dateTimeValue.toString())
                    ) {
                        const livingDeceasedCaseAssertion = currentCase.caseAssertions.find(
                            (x) =>
                                x.assertionDescriptorGuid?.toUpperCase() ===
                                AssertionDescriptors.LivingDeceased.Guid.toUpperCase()
                        );
                        if (
                            livingDeceasedCaseAssertion?.assertionDescriptorValueGuid &&
                            (!livingDeceasedCaseAssertion.userOverride ||
                                livingDeceasedCaseAssertion.assertionDescriptorValueGuid.toUpperCase() ===
                                    AssertionDescriptors.LivingDeceased.LivingADVGuid)
                        ) {
                            // If living/deceased value exists and its inferred by the system and not overriden by user, Estimate Age should be disabled.
                            // If living/deceased value exists and set to Living and a DOB exists, then system will infer Calc Age and hence Estimate Age should be disabled.
                            return true;
                        }
                    }
                }
                break;
            default:
        }

        return false;
    }

    static updateUserOverrideFieldForCaseAssertions(currentCase: ICaseModel) {
        if (currentCase.caseAssertions) {
            for (let i = 0; i < currentCase.caseAssertions.length; i++) {
                const currentItem = currentCase.caseAssertions[i];
                // Only store User Override column for Auto Calculated fields
                if (
                    currentItem.userOverride &&
                    CustomLogicAssertionDescriptors.filter(
                        (x) =>
                            x.Guid === currentItem.assertionDescriptorGuid!.toUpperCase() &&
                            x.AutoCalculated
                    ).length == 0
                ) {
                    currentItem.userOverride = undefined;
                }
            }
        }
    }

    static handleGroupFieldsForEasyUpdate(
        currentCase: ICaseModel,
        easyUpdateFieldsSelected: IMultiSelectOption[],
        adGroups: string[],
        assertionDescriptors: IAssertionDescriptorModel[]
    ) {
        const atLeastOneSelectedFieldBelongsToAnADGroup = easyUpdateFieldsSelected.some(
            (x) => x.guid && adGroups.some((y) => y === x.guid!.toUpperCase())
        );

        if (atLeastOneSelectedFieldBelongsToAnADGroup) {
            adGroups.forEach((adGroupGuid) => {
                const isNotSelected = !easyUpdateFieldsSelected.some(
                    (x) => x.guid?.toUpperCase() === adGroupGuid
                );

                const adMatch = assertionDescriptors.find(
                    (x) => x.guid?.toUpperCase() === adGroupGuid
                );

                if (isNotSelected) {
                    if (adMatch) {
                        easyUpdateFieldsSelected.push({
                            id: -1,
                            label: adMatch.displayName ?? adMatch.name!,
                            value: adMatch.name!,
                            guid: adMatch.guid!,
                            preventRemoval: true,
                        });

                        if (!currentCase.caseAssertions) currentCase.caseAssertions = [];

                        if (
                            !currentCase.caseAssertions.some(
                                (x) => x.assertionDescriptorGuid === adMatch.guid
                            )
                        ) {
                            const adCopy = cloneDeep(adMatch);
                            currentCase.caseAssertions.push({
                                assertionDescriptorGuid: adCopy.guid,
                            });
                        }
                    }
                } else {
                    const selectedField = easyUpdateFieldsSelected.find(
                        (field) => field.guid === adGroupGuid.toLowerCase()
                    );

                    if (selectedField) {
                        selectedField.preventRemoval = true;
                    }
                }
            });
        }
    }

    static getStaticItemFieldHelpText(staticItemId: number) {
        if (staticItemId === CaseFileStaticItems.CaseManager.Id)
            return 'Cases where the selected value is A1 or A2, a primary point of contact for a case\n\n“Blank” returns cases where A1 AND A2 fields are blank\n\n“Not Blank” returns cases where A1 AND/OR A2 fields have a value selected';

        for (let i = 0; i < QueryFilterDatabaseFieldsHelpText.length; i++) {
            if (staticItemId === QueryFilterDatabaseFieldsHelpText[i].id)
                return QueryFilterDatabaseFieldsHelpText[i].helpText;
        }
        return null;
    }

    static getAssertionDescriptorFieldHelpText(
        assertionDescriptors: IAssertionDescriptorModel[],
        name: string,
        assertionDescriptorGuid?: string,
        queryTool?: boolean,
        isSameCaseGroupPendingLockedField?: boolean,
        isSameCaseGroupApprovedLockedField?: boolean
    ) {
        if (assertionDescriptorGuid) {
            if (queryTool) {
                if (
                    assertionDescriptorGuid.toUpperCase() ===
                    AssertionDescriptors.PlainfirmFirmLocal.Guid.toUpperCase()
                )
                    return 'Cases where the selected firm is a Plaintiffs Firm or National Plaintiffs Firm\n\n“Blank” returns cases where Plaintiffs Firm AND National Plaintiffs Firm fields are blank\n\n“Not Blank” returns cases where Plaintiffs Firm AND/OR National Plaintiffs Firm fields have a value selected';
                else if (
                    assertionDescriptorGuid.toUpperCase() ===
                    AssertionDescriptors.Occupation.Guid.toUpperCase()
                )
                    return 'Cases where the selected Occupation is Occupation or Other Occupation\n\n“Blank” returns cases where Occupation AND Other Occupation fields are blank\n\n“Not Blank” returns cases where Occupation AND/OR Other Occupation fields have a value selected';
            }

            const match = assertionDescriptors.find(
                (x) =>
                    x.guid != undefined &&
                    x.guid.toUpperCase() == assertionDescriptorGuid.toUpperCase()
            );

            if (match && match.helpText) return match.helpText;
        }

        if (isSameCaseGroupPendingLockedField) {
            return SameCasePendingHelperText;
        } else if (isSameCaseGroupApprovedLockedField) {
            return SameCaseApprovedHelperText;
        }

        return null;
    }

    static getCaseTitle(currentCase: ICaseModel, excludeCaseGuid?: boolean) {
        const caseTitle: ICaseTitleModel = {
            guid: excludeCaseGuid ? undefined : currentCase.guid,
            caseName: currentCase.caseName,
            priority: currentCase.priority,
            SQProjection: currentCase.qProjection,
            SQScore: currentCase.qScore,
            threatScore: currentCase.threatScore,
        };
        return caseTitle;
    }

    static getCaseLocalCounselGuid = (currentCase: ICaseModel) => {
        if (currentCase.defenseCounsels) {
            const localCounsel = currentCase.defenseCounsels.find((x) => x.priority === 1);
            if (localCounsel) return localCounsel.guid;
        }
    };

    static getDefaultAlertTargetForCase = (
        user: IUserModel,
        currentCase: ICaseModel,
        currentZoneGuid?: string
    ) => {
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            user,
            currentZoneGuid
        );
        if (
            loggedInUserDataScopeId === DataScopesEnum.LocalBasic.Value &&
            currentCase.caseManagerUser
        ) {
            return currentCase.caseManagerUser ?? currentCase.alternateCaseManagerUser;
        }
    };

    static getDefaultWatchTargetsForCase = (
        user: IUserModel,
        currentCase: ICaseModel,
        currentZoneGuid?: string
    ) => {
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            user,
            currentZoneGuid
        );
        if (
            loggedInUserDataScopeId === DataScopesEnum.LocalBasic.Value &&
            currentCase.alternateCaseManagerUser
        ) {
            return [currentCase.alternateCaseManagerUser];
        }
    };

    // User "alternateDisplayName" everywhere BUT the Case File.
    static getCaseFileStaticItemDisplayName(lookupData: IStaticItemLookupModel[], item: number) {
        const match = lookupData.find((x) => x.id === item);
        if (match) return match.alternateDisplayName || match.displayName || match.name || '';

        return '';
    }

    static getAuthorizedQueryTypes(user: IUserModel, currentZoneGuid?: string) {
        const queryTypes: ILookupModel[] = [];

        if (Authorization.userHasRight(UserRightsEnum.ViewCaseFile, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.CaseDetails.Value,
                name: QueryTypes.CaseDetails.Name,
                displayName: QueryTypes.CaseDetails.DisplayName,
            });
        }

        if (Authorization.userHasRight(UserRightsEnum.ViewCaseNotes, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.CaseNotes.Value,
                name: QueryTypes.CaseNotes.Name,
                displayName: QueryTypes.CaseNotes.DisplayName,
            });
        }

        if (Authorization.userHasRight(UserRightsEnum.ViewCaseExpert, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.CaseExperts.Value,
                name: QueryTypes.CaseExperts.Name,
                displayName: QueryTypes.CaseExperts.DisplayName,
            });
        }

        if (Authorization.userHasRight(UserRightsEnum.ViewDeposition, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.Deposition.Value,
                name: QueryTypes.Deposition.Name,
                displayName: QueryTypes.Deposition.DisplayName,
            });
        }

        if (Authorization.userHasRight(UserRightsEnum.ViewZoneNotes, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.ZoneNotes.Value,
                name: QueryTypes.ZoneNotes.Name,
                displayName: QueryTypes.ZoneNotes.DisplayName,
            });
        }

        if (Authorization.userHasViewStrategyNotesRight(user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.StrategyNote.Value,
                name: QueryTypes.StrategyNote.Name,
                displayName: QueryTypes.StrategyNote.DisplayName,
            });
        }

        if (Authorization.userHasRight(UserRightsEnum.ViewPackageDealNote, user, currentZoneGuid)) {
            queryTypes.push({
                id: QueryTypes.PackageSettlementNotes.Value,
                name: QueryTypes.PackageSettlementNotes.Name,
                displayName: QueryTypes.PackageSettlementNotes.DisplayName,
            });
        }

        return queryTypes;
    }

    static setCaseAssertionValue(
        caseAssertionMatch: ICaseAssertionModel,
        assertionDescriptorMatch: IAssertionDescriptorModel,
        value: string
    ) {
        switch (assertionDescriptorMatch?.valueSourceType?.id) {
            case AssertionDescriptorValueSourceTypeEnum.Selection.Value:
                {
                    caseAssertionMatch.assertionDescriptorValueGuid =
                        value &&
                        assertionDescriptorMatch?.assertionDescriptorValues?.find(
                            (x) => x.guid === value
                        )
                            ? JSON.parse(
                                  JSON.stringify(
                                      assertionDescriptorMatch.assertionDescriptorValues.find(
                                          (x) => x.guid === value
                                      )
                                  )
                              )
                            : undefined;
                }
                break;
            case AssertionDescriptorValueSourceTypeEnum.Text.Value:
                switch (assertionDescriptorMatch?.valueDisplayType?.id) {
                    case AssertionDescriptorDisplayValueTypeEnum.DateTime.Value:
                        caseAssertionMatch.dateTimeValue = Common.safeDateFormatOrUndefined(value);
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.Number.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.Decimal.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.Percentage.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.Currency.Value:
                        caseAssertionMatch.numericValue = parseInt(value);
                        break;
                    case AssertionDescriptorDisplayValueTypeEnum.CurrencyWithCents.Value: {
                        const trimmed = value?.trim();
                        const currencyCentsVal: any = trimmed;
                        //Allow only numbers, - and precision, and any number of digits before/after decimal (no restriction)
                        const isValid = /^-?\d*\.?\d*$/.test(trimmed) || trimmed === '';

                        if (isValid) {
                            caseAssertionMatch.numericValue =
                                currencyCentsVal === '' ? '' : currencyCentsVal;
                        }
                        break;
                    }

                    case AssertionDescriptorDisplayValueTypeEnum.ShortText.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.Narrative.Value:
                    case AssertionDescriptorDisplayValueTypeEnum.SSN.Value:
                        caseAssertionMatch.textValue = value;
                        break;
                    default:
                        break;
                }
                break;
            case AssertionDescriptorValueSourceTypeEnum.Boolean.Value:
                if (value) caseAssertionMatch.booleanValue = value === 'true' ? true : false;
                break;
            default:
                break;
        }
    }
}
